import React, { useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";

import TextInput from "../../../../../Reusables/components/TextInput/TextInput";
import SelectInput from "../../../../../Reusables/components/SelectInput/SelectInput";
import CheckboxInput from "../../../../../Reusables/components/CheckboxInput/CheckboxInput";
import CustomButton from "../../../../reusable_components/custom_button_component/Button";
import alertModalStyles from "../../../../../Reusables/components/AlertModal/AlertModal.module.css";

import { AFFILIATE_TYPE_CODES } from "../../COIRegistry.types";
import { saveCOIRegistryEntry } from "./COIRegistryModal.service";
import { buildInitialFormState, validateCOIRegistryForm, buildCOIRegistryPayload } from "./COIRegistryModal.utils";
import { COIRegistryModalProps } from "./COIRegistryModal.types";
import styles from "./COIRegistryModal.module.css";

const AFFILIATE_TYPE_OPTIONS = [
  { id: AFFILIATE_TYPE_CODES.ENTITY, value: AFFILIATE_TYPE_CODES.ENTITY, label: "Entity" },
  { id: AFFILIATE_TYPE_CODES.INDIVIDUAL, value: AFFILIATE_TYPE_CODES.INDIVIDUAL, label: "Individual" },
];

const COIRegistryModal: React.FC<COIRegistryModalProps> = (props) => {
  const [form, setForm] = useState(buildInitialFormState(props.selectedRowData));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiInProgress, setApiInProgress] = useState(false);

  const handleTextChange = (fieldId: string) => (id: string, e: any) => {
    setForm((prev) => ({ ...prev, [fieldId]: e.target.value }));
  };

  const handleSave = async () => {
    const { valid, errors: validationErrors } = validateCOIRegistryForm(form);
    setErrors(validationErrors);
    if (!valid) return;

    setApiInProgress(true);
    try {
      const payload = buildCOIRegistryPayload(form, props.selectedRowData);
      const success = await saveCOIRegistryEntry(payload);
      if (success) {
        props.onHide(true);
      }
    } finally {
      setApiInProgress(false);
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={() => props.onHide(false)}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      enforceFocus={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={`${styles.content} ${alertModalStyles.content}`}
      backdropClassName={alertModalStyles.backdrop}
      aria-labelledby="coi-registry-modal"
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title id="coi-registry-modal" className={alertModalStyles.message}>
          {props.selectedRowData ? "Edit COI Registry Entry" : "Add COI Registry Entry"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={`${alertModalStyles.body} ${styles.body}`}>
        <Row className={styles.fieldRow}>
          <Col md={6}>
            <TextInput
              field={{
                id: "secClientId",
                type: "text",
                label: "SEC Client ID",
                placeholder: "e.g. PM20010.001",
                value: form.secClientId,
                error: errors.secClientId,
                isValid: !errors.secClientId,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
              }}
              handleChange={handleTextChange("secClientId")}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
          <Col md={6}>
            <SelectInput
              field={{
                id: "affiliateTypeCode",
                type: "select",
                label: "Affiliate Type",
                value: form.affiliateTypeCode,
                options: AFFILIATE_TYPE_OPTIONS,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
              }}
              handleChange={(fieldId, selected) =>
                setForm((prev) => ({ ...prev, affiliateTypeCode: selected.value }))
              }
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>

        {form.affiliateTypeCode === AFFILIATE_TYPE_CODES.ENTITY ? (
          <Row className={styles.fieldRow}>
            <Col md={12}>
              <TextInput
                field={{
                  id: "affiliateEntityName",
                  type: "text",
                  label: "Affiliate Entity Name",
                  value: form.affiliateEntityName,
                  error: errors.affiliateEntityName,
                  isValid: !errors.affiliateEntityName,
                  show: true,
                  validation: [{ type: "required", message: "Required!" }],
                }}
                handleChange={handleTextChange("affiliateEntityName")}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            </Col>
          </Row>
        ) : (
          <Row className={styles.fieldRow}>
            <Col md={4}>
              <TextInput
                field={{
                  id: "affiliateFirstName",
                  type: "text",
                  label: "Affiliate First Name",
                  value: form.affiliateFirstName,
                  error: errors.affiliateFirstName,
                  isValid: !errors.affiliateFirstName,
                  show: true,
                  validation: [{ type: "required", message: "Required!" }],
                }}
                handleChange={handleTextChange("affiliateFirstName")}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            </Col>
            <Col md={4}>
              <TextInput
                field={{
                  id: "affiliateMiddleName",
                  type: "text",
                  label: "Affiliate Middle Name",
                  value: form.affiliateMiddleName,
                  show: true,
                }}
                handleChange={handleTextChange("affiliateMiddleName")}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            </Col>
            <Col md={4}>
              <TextInput
                field={{
                  id: "affiliateLastName",
                  type: "text",
                  label: "Affiliate Last Name",
                  value: form.affiliateLastName,
                  error: errors.affiliateLastName,
                  isValid: !errors.affiliateLastName,
                  show: true,
                  validation: [{ type: "required", message: "Required!" }],
                }}
                handleChange={handleTextChange("affiliateLastName")}
                handleBlur={() => {}}
                handleSelect={() => {}}
              />
            </Col>
          </Row>
        )}

        <Row className={styles.fieldRow}>
          <Col md={12}>
            <TextInput
              field={{
                id: "relationshipToSECClient",
                type: "text",
                label: "Relationship To SEC Client",
                value: form.relationshipToSECClient,
                show: true,
              }}
              handleChange={handleTextChange("relationshipToSECClient")}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>

        <Row className={styles.fieldRow}>
          <Col md={12}>
            <TextInput
              field={{
                id: "comment",
                type: "text",
                label: "Comment",
                value: form.comment,
                show: true,
              }}
              handleChange={handleTextChange("comment")}
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>

        <Row className={styles.fieldRow}>
          <Col md={6}>
            <CheckboxInput
              field={{
                id: "affiliateIndividualSECClient",
                type: "checkbox",
                label: "Affiliate is also a SEC Client",
                value: form.affiliateIndividualSECClient,
                show: true,
              }}
              handleChange={(fieldId, e) =>
                setForm((prev) => ({ ...prev, affiliateIndividualSECClient: e.target.checked }))
              }
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
          <Col md={6}>
            <CheckboxInput
              field={{
                id: "coiRestriction",
                type: "checkbox",
                label: "COI Restriction Active",
                value: form.coiRestriction,
                show: true,
              }}
              handleChange={(fieldId, e) =>
                setForm((prev) => ({ ...prev, coiRestriction: e.target.checked }))
              }
              handleBlur={() => {}}
              handleSelect={() => {}}
            />
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className={alertModalStyles.footer}>
        <CustomButton content="Cancel" type="quinary" onclick={() => props.onHide(false)} />
        <CustomButton content="Save" type="tertiary" onclick={handleSave} progress={apiInProgress} />
      </Modal.Footer>
    </Modal>
  );
};

export default COIRegistryModal;
